(() => {
  if (window.__customConsultationScriptLoaded) return;
  window.__customConsultationScriptLoaded = true;

  const PENDING_STORAGE_KEY = 'custom-consultation:pending-submit';
  const PENDING_STATE_TTL = 15 * 60 * 1000;
  const DEFAULT_DIAL_CODE = '+380';
  const UA_DIAL_CODE = '+380';
  const UA_PHONE_DIGITS = 9;
  const FALLBACK_MIN_PHONE_DIGITS = 6;
  const FALLBACK_MAX_PHONE_DIGITS = 14;
  const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  function getWebhookUrl(root) {
    return (
      root.dataset.webhookUrl ||
      window.customConsultationConfig?.webhookUrl ||
      ''
    ).trim();
  }

  function setPendingState(state) {
    try {
      sessionStorage.setItem(
        PENDING_STORAGE_KEY,
        JSON.stringify({ ...state, createdAt: Date.now() })
      );
    } catch (error) {}
  }

  function getPendingState() {
    try {
      const raw = sessionStorage.getItem(PENDING_STORAGE_KEY);
      if (!raw) return null;

      const state = JSON.parse(raw);
      const isFresh =
        Date.now() - Number(state.createdAt || 0) <= PENDING_STATE_TTL;

      if (!isFresh) {
        sessionStorage.removeItem(PENDING_STORAGE_KEY);
        return null;
      }

      return state;
    } catch (error) {
      return null;
    }
  }

  function clearPendingState() {
    try {
      sessionStorage.removeItem(PENDING_STORAGE_KEY);
    } catch (error) {}
  }

  function sendToWebhook(root, payload) {
    const webhookUrl = getWebhookUrl(root);
    if (!webhookUrl) return;

    const body = JSON.stringify(payload);

    try {
      if (navigator.sendBeacon) {
        const blob = new Blob([body], { type: 'text/plain;charset=utf-8' });
        const queued = navigator.sendBeacon(webhookUrl, blob);
        if (queued) return;
      }
    } catch (error) {}

    fetch(webhookUrl, {
      method: 'POST',
      mode: 'no-cors',
      headers: {
        'Content-Type': 'text/plain;charset=utf-8'
      },
      body,
      keepalive: true
    }).catch(() => {});
  }

  function sanitizeDialCode(value) {
    const digits = String(value || '').replace(/[^\d]/g, '');
    return digits ? `+${digits}` : '';
  }

  function sanitizePhoneNumber(value) {
    return String(value || '').replace(/\D/g, '');
  }

  function normalizeReferenceUrl(value) {
    const trimmed = String(value || '').trim();

    if (!trimmed) return '';
    if (/^[a-z][a-z0-9+.-]*:\/\//i.test(trimmed)) return trimmed;
    if (/^\/\//.test(trimmed)) return `https:${trimmed}`;

    return `https://${trimmed.replace(/^\/+/, '')}`;
  }

  function buildLeadEventPayload(payload = {}) {
    const leadType = payload.product_title
      ? 'product_consultation'
      : payload.reference_url
        ? 'reference_request'
        : 'consultation_request';

    return {
      event: 'generate_lead',
      lead_type: leadType,
      form_source: payload.form_source || '',

      product_title: payload.product_title || '',
      chosen_variant: payload.chosen_variant || '',
      product_url: payload.product_url || '',
      reference_url: payload.reference_url || '',

      name: payload.name || '',
      company: payload.company || '',
      email: payload.email || '',
      phone: payload.phone || '',
      country: payload.country || '',
      estimated_quantity: payload.estimated_quantity || ''
    };
  }

  function pushLeadEventToGtm(payload = {}) {
    try {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push(buildLeadEventPayload(payload));
    } catch (error) {}
  }

  class CustomConsultation {
    constructor(root) {
      this.root = root;
      this.dialog = root.querySelector('[data-consultation-dialog]');
      this.openButton = root.querySelector('[data-consultation-open]');
      this.closeButtons = root.querySelectorAll('[data-consultation-close]');
      this.form = root.querySelector('.product-consultation__form, form');

      this.inlineSuccess = root.querySelector('[data-consultation-inline-success]');
      this.errorsBox = root.querySelector('[data-consultation-errors]');
      this.formContent = root.querySelector('[data-consultation-form-content]');
      this.successBox = root.querySelector('[data-consultation-success]');
      this.title = root.querySelector('[data-consultation-title]');
      this.topbar = root.querySelector('.product-consultation__topbar');
      this.submitButton = root.querySelector('[data-consultation-submit]');

      this.serverPostedSuccessfully =
        root.querySelector('[data-server-posted-successfully]')?.value === 'true';
      this.serverHasErrors =
        root.querySelector('[data-server-has-errors]')?.value === 'true';

      this.emailInput = root.querySelector('[data-email-input], [name="contact[email]"]');
      this.dialCodeInput = root.querySelector('[data-dial-code-input]');
      this.phoneInput = root.querySelector('[data-phone-input]');
      this.phoneHidden = root.querySelector('[data-phone-hidden]');
      this.countryInput = root.querySelector('[data-country-input]');
      this.countryHidden = root.querySelector('[data-country-hidden]');
      this.quantityInput = root.querySelector('[data-quantity-input]');
      this.quantityMinus = root.querySelector('[data-qty-minus]');
      this.quantityPlus = root.querySelector('[data-qty-plus]');
      this.referenceUrlInput = root.querySelector('[data-reference-url-input], [name="contact[Reference URL]"]');

      this.productMode = root.dataset.productMode === 'true';
      this.formSource =
        this.form?.querySelector('[name="contact[Form source]"]')?.value?.trim() ||
        root.dataset.formSource ||
        'Custom consultation';

      this.productForm = this.findProductForm();
      this.variantIdInput = this.productForm?.querySelector('[name="id"]');
      this.variantMap = this.getJsonData('[data-consultation-variants]') || {};
      this.productImage = root.querySelector('[data-consultation-product-image]');
      this.productPrice = root.querySelector('[data-consultation-product-price]');
      this.variantLabel = root.querySelector('[data-consultation-variant-label]');
      this.hiddenVariantTitle = root.querySelector('[data-hidden-variant-title]');
      this.hiddenProductTitle = root.querySelector('[data-hidden-product-title]');
      this.hiddenProductUrl = root.querySelector('[data-hidden-product-url], [name="contact[Product URL]"]');

      this.init();
    }

    getJsonData(selector) {
      const node = this.root.querySelector(selector);
      if (!node) return null;

      try {
        return JSON.parse(node.textContent);
      } catch (error) {
        return null;
      }
    }

    findProductForm() {
      if (!this.productMode) return null;
      const section = this.root.closest('.shopify-section');
      return (
        section?.querySelector('form[action*="/cart/add"]') ||
        document.querySelector('form[action*="/cart/add"]')
      );
    }

    init() {
      this.bindModal();
      this.bindSubmit();
      this.bindPhone();
      this.bindEmailValidation();
      this.bindQuantity();
      this.bindReferenceUrl();

      if (this.productMode) {
        this.bindVariantSync();
        this.syncVariant();
      }

      this.setInitialDialCode();
      this.hydrateServerResultState();
      this.restorePostedState();
      this.cleanUrlParams();

      if (this.dialog?.dataset.openOnLoad === 'true') {
        this.openDialog();
      }
    }

    bindModal() {
      if (!this.dialog || !this.openButton) return;

      this.openButton.addEventListener('click', () => this.openDialog());

      this.closeButtons.forEach((button) => {
        button.addEventListener('click', () => this.closeDialog());
      });

      this.dialog.addEventListener('cancel', (event) => {
        event.preventDefault();
        this.closeDialog();
      });

      this.dialog.addEventListener('click', (event) => {
        if (event.target === this.dialog) {
          this.closeDialog();
        }
      });

      this.dialog.addEventListener('keydown', (event) => {
        if (event.key !== 'Enter') return;

        const target = event.target;
        const tagName = target?.tagName;
        const type = target?.getAttribute?.('type');

        if (tagName === 'TEXTAREA' || type === 'submit') return;
        event.preventDefault();
      });
    }

    openDialog() {
      if (!this.dialog) return;

      if (typeof this.dialog.showModal === 'function' && !this.dialog.open) {
        this.dialog.showModal();
      } else {
        this.dialog.setAttribute('open', 'open');
      }

      if (this.productMode) {
        this.syncVariant();
      }

      this.setInitialDialCode();
    }

    closeDialog() {
      if (!this.dialog) return;

      if (typeof this.dialog.close === 'function' && this.dialog.open) {
        this.dialog.close();
      } else {
        this.dialog.removeAttribute('open');
      }

      this.openButton?.focus();
    }

    bindSubmit() {
      if (!this.form) return;

      this.form.addEventListener('submit', (event) => {
        this.prepareFormValues();

        if (!this.validateForm()) {
          event.preventDefault();
          event.stopPropagation();
          return;
        }

        if (this.submitButton?.disabled) {
          event.preventDefault();
          event.stopPropagation();
          return;
        }

        if (this.submitButton) {
          this.submitButton.disabled = true;
          this.submitButton.setAttribute('aria-busy', 'true');
        }

        const webhookPayload = this.buildWebhookPayload();

        setPendingState({
          formId: this.form.id || '',
          formSource: this.formSource,
          path: window.location.pathname,
          gtmPayload: buildLeadEventPayload(webhookPayload)
        });

        sendToWebhook(this.root, webhookPayload);

        window.setTimeout(() => {
          this.unlockSubmitButton();
        }, 8000);
      });
    }

    bindPhone() {
      if (this.dialCodeInput) {
        this.dialCodeInput.addEventListener('input', () => {
          this.dialCodeInput.value = sanitizeDialCode(this.dialCodeInput.value);

          if (!this.dialCodeInput.value) {
            this.dialCodeInput.value = DEFAULT_DIAL_CODE;
          }

          this.applyPhoneInputLimit();
          this.validatePhoneField();
        });

        this.dialCodeInput.addEventListener('blur', () => {
          this.dialCodeInput.value = sanitizeDialCode(this.dialCodeInput.value) || DEFAULT_DIAL_CODE;
          this.applyPhoneInputLimit();
          this.validatePhoneField();
        });
      }

      if (this.phoneInput) {
        const handlePhoneInput = () => {
          this.phoneInput.value = sanitizePhoneNumber(this.phoneInput.value);
          this.applyPhoneInputLimit();
          this.validatePhoneField();
        };

        this.phoneInput.addEventListener('input', handlePhoneInput);
        this.phoneInput.addEventListener('blur', handlePhoneInput);
      }
    }

    bindEmailValidation() {
      if (!this.emailInput) return;

      this.emailInput.addEventListener('input', () => this.validateEmailField());
      this.emailInput.addEventListener('blur', () => this.validateEmailField());
    }

    bindQuantity() {
      if (!this.quantityInput) return;

      this.quantityMinus?.addEventListener('click', () => {
        const currentValue = parseInt(this.quantityInput.value || '1', 10);
        const min = parseInt(this.quantityInput.min || '1', 10);
        this.quantityInput.value = String(Math.max(min, currentValue - 1));
      });

      this.quantityPlus?.addEventListener('click', () => {
        const currentValue = parseInt(this.quantityInput.value || '1', 10);
        this.quantityInput.value = String(currentValue + 1);
      });
    }

    bindReferenceUrl() {
      if (!this.referenceUrlInput) return;

      this.referenceUrlInput.addEventListener('blur', () => {
        this.referenceUrlInput.value = normalizeReferenceUrl(this.referenceUrlInput.value);
      });
    }

    bindVariantSync() {
      if (!this.productForm || !this.variantIdInput) return;

      const sync = () => {
        window.requestAnimationFrame(() => this.syncVariant());
      };

      this.productForm.addEventListener('change', sync);
      this.productForm.addEventListener('input', sync);

      const observer = new MutationObserver(sync);
      observer.observe(this.variantIdInput, {
        attributes: true,
        attributeFilter: ['value']
      });
    }

    getCurrentVariantData() {
      const variantId = this.variantIdInput?.value;
      if (!variantId || !this.variantMap[variantId]) return null;

      return {
        id: variantId,
        ...this.variantMap[variantId]
      };
    }

    syncVariant() {
      const variant = this.getCurrentVariantData();
      if (!variant) return;

      if (this.productImage && variant.image) {
        this.productImage.src = variant.image;
      }

      if (this.productPrice && variant.price) {
        this.productPrice.textContent = variant.price;
      }

      const variantTitle = variant.title === 'Default Title' ? '' : variant.title;

      if (this.variantLabel) {
        this.variantLabel.textContent = variantTitle;
        this.variantLabel.hidden = variantTitle === '';
      }

      if (this.hiddenVariantTitle) {
        this.hiddenVariantTitle.value = variantTitle;
      }
    }

    setInitialDialCode() {
      if (!this.dialCodeInput) return;

      this.dialCodeInput.value = DEFAULT_DIAL_CODE;
      this.applyPhoneInputLimit();
    }

    applyPhoneInputLimit() {
      if (!this.phoneInput) return;

      const dialCode = sanitizeDialCode(this.dialCodeInput?.value || DEFAULT_DIAL_CODE);
      const maxDigits = dialCode === UA_DIAL_CODE ? UA_PHONE_DIGITS : FALLBACK_MAX_PHONE_DIGITS;

      this.phoneInput.maxLength = maxDigits;
      this.phoneInput.value = sanitizePhoneNumber(this.phoneInput.value).slice(0, maxDigits);
    }

    validateEmailField() {
      if (!this.emailInput) return true;

      const value = this.emailInput.value.trim();
      this.emailInput.setCustomValidity('');

      if (!value) {
        return !this.emailInput.required;
      }

      if (!EMAIL_PATTERN.test(value)) {
        this.emailInput.setCustomValidity('Please enter a valid email address.');
        return false;
      }

      return true;
    }

    validatePhoneField() {
      if (!this.phoneInput) return true;

      const digits = sanitizePhoneNumber(this.phoneInput.value);
      const dialCode = sanitizeDialCode(this.dialCodeInput?.value || DEFAULT_DIAL_CODE);

      this.phoneInput.setCustomValidity('');
      if (this.dialCodeInput) {
        this.dialCodeInput.setCustomValidity('');
      }

      if (!dialCode) {
        if (this.dialCodeInput) {
          this.dialCodeInput.setCustomValidity('Please enter a country code.');
        }
        return false;
      }

      if (!digits) {
        return !this.phoneInput.required;
      }

      if (dialCode === UA_DIAL_CODE) {
        if (digits.length !== UA_PHONE_DIGITS) {
          this.phoneInput.setCustomValidity('Please enter exactly 9 digits after +380.');
          return false;
        }

        return true;
      }

      if (digits.length < FALLBACK_MIN_PHONE_DIGITS || digits.length > FALLBACK_MAX_PHONE_DIGITS) {
        this.phoneInput.setCustomValidity('Please enter a valid phone number.');
        return false;
      }

      return true;
    }

    validateForm() {
      const isEmailValid = this.validateEmailField();
      const isPhoneValid = this.validatePhoneField();

      if (!isEmailValid) {
        this.emailInput?.reportValidity();
        return false;
      }

      if (!isPhoneValid) {
        (this.phoneInput?.validationMessage ? this.phoneInput : this.dialCodeInput)?.reportValidity();
        return false;
      }

      return this.form ? this.form.checkValidity() : true;
    }

    prepareFormValues() {
      if (this.countryInput && this.countryHidden) {
        this.countryHidden.value = this.countryInput.value.trim();
      }

      if (this.phoneInput) {
        this.phoneInput.value = sanitizePhoneNumber(this.phoneInput.value);
        this.applyPhoneInputLimit();
      }

      if (this.dialCodeInput) {
        this.dialCodeInput.value = sanitizeDialCode(this.dialCodeInput.value) || DEFAULT_DIAL_CODE;
      }

      if (this.phoneHidden) {
        const dialCode = sanitizeDialCode(this.dialCodeInput?.value || DEFAULT_DIAL_CODE);
        const phone = sanitizePhoneNumber(this.phoneInput?.value || '');
        this.phoneHidden.value = [dialCode, phone].filter(Boolean).join('').trim();
      }

      if (this.hiddenVariantTitle && this.productMode) {
        const selectedVariantTitle =
          this.variantLabel?.textContent.trim() ||
          (this.getCurrentVariantData()?.title === 'Default Title'
            ? ''
            : this.getCurrentVariantData()?.title || '');

        this.hiddenVariantTitle.value = selectedVariantTitle;
      }

      if (this.referenceUrlInput) {
        this.referenceUrlInput.value = normalizeReferenceUrl(this.referenceUrlInput.value);
      }
    }

    buildWebhookPayload() {
      const payload = {
        submitted_at: new Date().toISOString(),
        form_source: this.formSource,
        name: this.form?.querySelector('[name="contact[Name]"]')?.value?.trim() || '',
        company: this.form?.querySelector('[name="contact[Company]"]')?.value?.trim() || '',
        email: this.form?.querySelector('[name="contact[email]"]')?.value?.trim() || '',
        phone: this.phoneHidden?.value?.trim() || '',
        country: this.countryHidden?.value?.trim() || '',
        estimated_quantity: this.form?.querySelector('[name="contact[Estimated quantity]"]')?.value?.trim() || '',
        reference_url: normalizeReferenceUrl(this.referenceUrlInput?.value || ''),
        message: this.form?.querySelector('[name="contact[body]"]')?.value?.trim() || ''
      };

      if (this.productMode) {
        payload.product_title = this.hiddenProductTitle?.value?.trim() || '';
        payload.chosen_variant = this.hiddenVariantTitle?.value?.trim() || '';
        payload.product_url = this.hiddenProductUrl?.value?.trim() || '';
      }

      return payload;
    }

    hydrateServerResultState() {
      const pendingState = getPendingState();
      const hasServerState = this.serverPostedSuccessfully || this.serverHasErrors;

      if (!hasServerState) return;

      const isOwnSubmit =
        pendingState &&
        pendingState.formId === (this.form?.id || '') &&
        pendingState.formSource === this.formSource &&
        (!pendingState.path || pendingState.path === window.location.pathname);

      if (isOwnSubmit) return;

      this.serverPostedSuccessfully = false;
      this.serverHasErrors = false;

      this.inlineSuccess?.setAttribute('hidden', 'hidden');
      this.errorsBox?.setAttribute('hidden', 'hidden');
      this.successBox?.setAttribute('hidden', 'hidden');
      this.formContent?.removeAttribute('hidden');

      if (this.title?.dataset.defaultHeading) {
        this.title.textContent = this.title.dataset.defaultHeading;
        this.title.classList.remove('product-consultation__title--success');
      }

      this.topbar?.classList.remove('product-consultation__topbar--success');

      if (this.dialog) {
        this.dialog.dataset.openOnLoad = 'false';

        if (typeof this.dialog.close === 'function' && this.dialog.open) {
          this.dialog.close();
        } else {
          this.dialog.removeAttribute('open');
        }
      }
    }

    showSuccessState() {
      this.inlineSuccess?.removeAttribute('hidden');
      this.errorsBox?.setAttribute('hidden', 'hidden');
      this.formContent?.setAttribute('hidden', 'hidden');
      this.successBox?.removeAttribute('hidden');

      if (this.title) {
        this.title.textContent = this.title.dataset.successHeading || this.title.textContent;
        this.title.classList.add('product-consultation__title--success');
      }

      this.topbar?.classList.add('product-consultation__topbar--success');
    }

    resetFormFields() {
      this.form?.reset();

      if (this.quantityInput) {
        this.quantityInput.value = '1';
      }

      if (this.phoneHidden) {
        this.phoneHidden.value = '';
      }

      if (this.countryHidden) {
        this.countryHidden.value = '';
      }

      this.setInitialDialCode();

      if (this.productMode) {
        this.syncVariant();
      }
    }

    unlockSubmitButton() {
      if (!this.submitButton) return;
      this.submitButton.disabled = false;
      this.submitButton.removeAttribute('aria-busy');
    }

    restorePostedState() {
      const state = getPendingState();
      if (!state) return;

      const isThisForm =
        state.formId === (this.form?.id || '') &&
        state.formSource === this.formSource &&
        (!state.path || state.path === window.location.pathname);

      if (!isThisForm) return;

      this.unlockSubmitButton();

      if (this.serverPostedSuccessfully) {
        pushLeadEventToGtm(state.gtmPayload || {});
        clearPendingState();
        this.openDialog();
        this.showSuccessState();
        this.resetFormFields();
        return;
      }

      if (this.serverHasErrors) {
        clearPendingState();
        this.openDialog();
      }
    }

    cleanUrlParams() {
      const url = new URL(window.location.href);
      let changed = false;

      ['contact_posted', 'form_type'].forEach((param) => {
        if (url.searchParams.has(param)) {
          url.searchParams.delete(param);
          changed = true;
        }
      });

      if (changed) {
        const query = url.searchParams.toString();
        const nextUrl = `${url.pathname}${query ? `?${query}` : ''}${url.hash}`;
        window.history.replaceState({}, document.title, nextUrl);
      }
    }
  }

  function initCustomConsultations(scope = document) {
    scope.querySelectorAll('[data-consultation-root]').forEach((root) => {
      if (root.dataset.consultationReady === 'true') return;
      root.dataset.consultationReady = 'true';
      new CustomConsultation(root);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initCustomConsultations(document));
  } else {
    initCustomConsultations(document);
  }

  document.addEventListener('shopify:section:load', (event) => {
    initCustomConsultations(event.target);
  });
})();

